import type { LineString, MultiLineString } from 'geojson';
import L from 'leaflet';
import proj4 from 'proj4';
import { toWgs84 } from 'reproject';
import { ArrowPolyLine } from './ArrowPolyLine';
import type { ClosedRoadLayerOptions } from './Layers.types';

const SWEREF99_PROJ = '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';
const CLOSED_ROAD_PANE = 'closed-road-pane';

const defaultOptions: Partial<ClosedRoadLayerOptions> = {
    weight: 4,
    color: 'var(--cc-map-road-closed-stroke)',
    baseColor: 'var(--cc-map-road-closed-base)',
    dashArray: '2 8', //First number is the length of the dash, second number is the length of the gap.
};

interface ClosedRoadLayer {
    options: ClosedRoadLayerOptions;
}

//Renders road as closed with a red-dotted line, stays visible as long as the situation is on the map.
class ClosedRoadLayer extends L.LayerGroup {
    private readonly _geometry: LineString | MultiLineString;
    private _renderer: L.SVG;

    constructor(options: ClosedRoadLayerOptions) {
        super([], { ...defaultOptions, ...options });

        this._geometry = toWgs84(this.options.feature.properties.sweRef99Extent, SWEREF99_PROJ, proj4) as LineString | MultiLineString;
    }

    onAdd(map: L.Map): this {
        if (!map.getPane(CLOSED_ROAD_PANE)) {
            const pane = map.createPane(CLOSED_ROAD_PANE);
            // Above the travel time routes (overlayPane, z-index 400) so closures hide the route beneath.
            pane.style.zIndex = '450';
            pane.style.pointerEvents = 'none';
        }
        this._renderer = L.svg({ pane: CLOSED_ROAD_PANE });

        super.onAdd(map);

        map.on('zoomend', this.redraw, this);
        this.redraw();

        return this;
    }

    onRemove(map: L.Map): this {
        map.off('zoomend', this.redraw, this);
        this.clearLayers();

        return super.onRemove(map);
    }

    private redraw = (): void => {
        if (!this._map) {
            return;
        }

        this.clearLayers();

        const weight = this.options.weight;
        // Keep the opaque base wider than the 5 px information layer.
        const casingWeight = this.getCasingWeight(weight);

        // Opaque base line hides the underlying travel time route and information layer.
        const baseStyle: L.PolylineOptions = {
            renderer: this._renderer,
            pane: CLOSED_ROAD_PANE,
            interactive: false,
            color: this.options.baseColor,
            opacity: 1,
            weight: casingWeight,
            lineCap: 'butt',
        };
        this.addLayer(L.GeoJSON.geometryToLayer(L.GeoJSON.asFeature(this._geometry), baseStyle));
        // Red dotted line plus the direction indicator, reusing ArrowPolyLine.
        this.addLayer(
            new ArrowPolyLine(this._geometry, {
                arrowDirection: this.options.feature.properties.affectedDirection.toLowerCase(),
                arrowPane: CLOSED_ROAD_PANE,
                style: {
                    renderer: this._renderer,
                    pane: CLOSED_ROAD_PANE,
                    interactive: false,
                    color: this.options.color,
                    opacity: 1,
                    weight: weight,
                    dashArray: this.options.dashArray,
                    lineCap: 'square',
                },
            }),
        );
    };

    // Retain additional growth at the two closest zoom levels, where travel geometries can differ from situation extent.
    private getCasingWeight(weight: number): number {
        const isAtHighestZoomLevels = this._map.getZoom() >= this._map.getMaxZoom() - 1;
        return weight + 2 + (isAtHighestZoomLevels ? 4 : 0);
    }
}

export { ClosedRoadLayer };
